import React, { useState, useRef, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ControlPanel } from './ControlPanel';
import { DetailPanel } from './DetailPanel';
import { ProgressBar } from './ProgressBar';
import { InfoModal } from './InfoModal';
import { 
  calculateSummitScore, 
  calculateComfortScore, 
  filterTopSummits, 
  filterTopComforts,
  getScoreColor,
  getLOSLabel
} from '../utils/scoreCalculator';
import { 
  fetchElevationBatch, 
  fetchOSMPeaks, 
  fetchOSMPicnicSites
} from '../utils/apiHelpers';
import { latLngToMaidenhead } from '../utils/maidenhead';
import { toast } from 'sonner';
import { Target, Mountain, TreePine, Compass, Info } from 'lucide-react';

// LocalStorage key
const CACHE_KEY = 'hambestspots_cache';
const CACHE_VERSION = 'v2';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

// Custom icons for different spot types
const summitIcon = new L.DivIcon({
  className: 'custom-marker-icon',
  html: `<div style="
    width: 32px; 
    height: 32px; 
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); 
    border-radius: 50% 50% 50% 0; 
    transform: rotate(-45deg);
    border: 2px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
  ">
    <svg style="transform: rotate(45deg); width: 16px; height: 16px; color: white;" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12 2L2 22h20L12 2zm0 4l7 14H5l7-14z"/>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const comfortIcon = new L.DivIcon({
  className: 'custom-marker-icon',
  html: `<div style="
    width: 32px; 
    height: 32px; 
    background: linear-gradient(135deg, #10b981 0%, #059669 100%); 
    border-radius: 50% 50% 50% 0; 
    transform: rotate(-45deg);
    border: 2px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
  ">
    <svg style="transform: rotate(45deg); width: 16px; height: 16px; color: white;" fill="currentColor" viewBox="0 0 24 24">
      <path d="M17 12h2a1 1 0 011 1v8a1 1 0 01-1 1h-6v-4a2 2 0 00-2-2 2 2 0 00-2 2v4H3a1 1 0 01-1-1v-8a1 1 0 011-1h2V9a1 1 0 011-1h2V5a3 3 0 016 0v3h2a1 1 0 011 1v3z"/>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

const centerIcon = new L.DivIcon({
  className: 'center-marker-icon',
  html: `<div style="
    width: 24px; 
    height: 24px; 
    background: #06b6d4; 
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 2px 8px rgba(0,0,0,0.3);
  "></div>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

// Component to handle map clicks and recenter
const MapController = ({ onMapClick, center, radius }) => {
  const map = useMap();

  useMapEvents({
    click: (e) => {
      onMapClick([e.latlng.lat, e.latlng.lng]);
    },
  });

  // Recenter map when center changes
  useEffect(() => {
    if (center && map) {
      map.flyTo(center, map.getZoom(), { duration: 0.5 });
    }
  }, [center, map]);

  return (
    <>
      <Marker position={center} icon={centerIcon}>
        <Popup>
          <div className="font-mono text-xs">
            <div className="font-bold mb-1 text-cyan-600">Centre de recherche</div>
            <div>Locator: {latLngToMaidenhead(center[0], center[1])}</div>
            <div>{center[0].toFixed(5)}°N, {center[1].toFixed(5)}°E</div>
          </div>
        </Popup>
      </Marker>
      <Circle
        center={center}
        radius={radius * 1000}
        pathOptions={{
          color: '#06b6d4',
          fillColor: '#06b6d4',
          fillOpacity: 0.05,
          weight: 2,
          dashArray: '8, 12',
        }}
      />
    </>
  );
};

// Cache helpers
const getCacheKey = (lat, lng, radius) => {
  // Round coordinates to reduce cache misses for nearby searches
  const roundedLat = Math.round(lat * 100) / 100;
  const roundedLng = Math.round(lng * 100) / 100;
  return `${CACHE_VERSION}_${roundedLat}_${roundedLng}_${radius}`;
};

const loadFromCache = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      return JSON.parse(cached);
    }
  } catch (e) {
    console.error('Error loading cache:', e);
  }
  return {};
};

const saveToCache = (cache) => {
  try {
    // Limit cache size to 20 searches
    const keys = Object.keys(cache);
    if (keys.length > 20) {
      const keysToRemove = keys.slice(0, keys.length - 20);
      keysToRemove.forEach(key => delete cache[key]);
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch (e) {
    console.error('Error saving cache:', e);
  }
};

export const MapView = () => {
  const [center, setCenter] = useState([46.2044, 6.1432]); // Geneva default
  const [radius, setRadius] = useState(25);
  const [zoom, setZoom] = useState(10);
  const [isCalculating, setIsCalculating] = useState(false);
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showSummits, setShowSummits] = useState(true);
  const [showComforts, setShowComforts] = useState(true);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState('');
  const [cache, setCache] = useState(() => loadFromCache());
  const mapRef = useRef(null);

  // Combine all cached spots for display
  const allCachedSpots = useMemo(() => {
    const summits = [];
    const comforts = [];
    
    Object.values(cache).forEach(cached => {
      if (cached.summits) {
        cached.summits.forEach(s => {
          // Avoid duplicates based on coordinates
          if (!summits.some(existing => existing.lat === s.lat && existing.lng === s.lng)) {
            summits.push(s);
          }
        });
      }
      if (cached.comforts) {
        cached.comforts.forEach(c => {
          if (!comforts.some(existing => existing.lat === c.lat && existing.lng === c.lng)) {
            comforts.push(c);
          }
        });
      }
    });
    
    return { summits, comforts };
  }, [cache]);

  // Reset cache function
  const handleResetCache = () => {
    setCache({});
    localStorage.removeItem(CACHE_KEY);
  };

  const handleMapClick = (coords) => {
    setCenter(coords);
    const locator = latLngToMaidenhead(coords[0], coords[1]);
    toast.success(`Centre défini: ${locator}`);
  };

  const handleCenterChange = (coords) => {
    setCenter(coords);
  };

  const startSearch = async () => {
    const startTime = Date.now();
    setIsCalculating(true);
    setProgress(0);
    setProgressMessage('Initialisation...');

    try {
      const radiusMeters = radius * 1000;
      const cacheKey = getCacheKey(center[0], center[1], radius);

      // Check cache first
      if (cache[cacheKey]) {
        toast.info('Zone déjà en cache !');
        setProgress(100);
        setProgressMessage('Chargé depuis le cache !');
        
        setTimeout(() => {
          setIsCalculating(false);
          setProgress(0);
        }, 500);
        
        toast.success(`${cache[cacheKey].summits.length} points hauts + ${cache[cacheKey].comforts.length} conforts (cache)`);
        return;
      }

      // Step 1: Fetch OSM Peaks (Summits)
      setProgressMessage('Recherche des sommets OSM...');
      setProgress(10);
      const peaks = await fetchOSMPeaks(center[0], center[1], radiusMeters);
      
      // Step 2: Fetch OSM Picnic Sites (Comfort)
      setProgressMessage('Recherche des aires de pique-nique...');
      setProgress(30);
      const picnicSites = await fetchOSMPicnicSites(center[0], center[1], radiusMeters);
      
      toast.info(`${peaks.length} sommets + ${picnicSites.length} conforts trouvés`);

      if (peaks.length === 0 && picnicSites.length === 0) {
        toast.error('Aucun spot trouvé. Essayez un rayon plus grand.');
        setIsCalculating(false);
        setProgress(0);
        return;
      }

      // Step 3: Fetch elevations for points without elevation data
      setProgressMessage('Récupération des altitudes...');
      setProgress(50);
      
      const peaksNeedingElevation = peaks.filter(p => !p.elevation);
      const comfortsNeedingElevation = picnicSites;
      
      const allPointsForElevation = [
        ...peaksNeedingElevation,
        ...comfortsNeedingElevation
      ];

      let elevationData = [];
      if (allPointsForElevation.length > 0) {
        elevationData = await fetchElevationBatch(allPointsForElevation);
      }

      // Apply elevations
      let elevationIdx = 0;
      const peaksWithElevation = peaks.map(peak => {
        if (!peak.elevation) {
          return { ...peak, elevation: elevationData[elevationIdx++] || 0 };
        }
        return peak;
      });

      const comfortsWithElevation = picnicSites.map(site => ({
        ...site,
        elevation: elevationData[elevationIdx++] || 0
      }));

      // Step 4: Calculate scores for summits with LOS
      setProgressMessage('Calcul des scores et LOS...');
      setProgress(70);
      
      const scoredSummits = peaksWithElevation.map(peak => {
        const result = calculateSummitScore(peak, {}, peaksWithElevation);
        return {
          ...peak,
          score: result.score,
          losData: result.losData,
          qrmData: {}
        };
      });

      // Step 5: Calculate scores for comfort spots with LOS
      setProgressMessage('Calcul des scores confort + LOS...');
      setProgress(85);
      
      const scoredComforts = comfortsWithElevation.map(site => {
        // Combine all points for LOS calculation
        const allPointsForLOS = [...peaksWithElevation, ...comfortsWithElevation];
        const result = calculateComfortScore(site, {
          hasRoadAccess: true,
          hasParking: false,
          hasViewpoint: false,
          hasShelter: site.hasShelter || false,
          hasToilets: false,
          hasWater: false,
        }, allPointsForLOS);
        return {
          ...site,
          score: result.score,
          losData: result.losData,
          amenityData: {
            hasRoadAccess: true,
            hasShelter: site.hasShelter || false,
          }
        };
      });

      // Step 6: Filter to top spots
      setProgressMessage('Sélection des meilleurs spots...');
      setProgress(95);
      
      const topSummits = filterTopSummits(scoredSummits, 5);
      const topComforts = filterTopComforts(scoredComforts, 5);

      // Save to cache - this will automatically update allCachedSpots via useMemo
      const newCache = {
        ...cache,
        [cacheKey]: {
          summits: topSummits,
          comforts: topComforts,
          timestamp: Date.now()
        }
      };
      setCache(newCache);
      saveToCache(newCache);

      setProgress(100);
      setProgressMessage('Terminé !');
      
      const elapsed = Math.round((Date.now() - startTime) / 1000);
      toast.success(`${topSummits.length} points hauts + ${topComforts.length} conforts en ${elapsed}s`);

      setTimeout(() => {
        setIsCalculating(false);
        setProgress(0);
      }, 800);

    } catch (error) {
      console.error('Search error:', error);
      toast.error('Erreur lors de la recherche. Veuillez réessayer.');
      setIsCalculating(false);
      setProgress(0);
    }
  };

  const handlePointClick = (point) => {
    setSelectedPoint(point);
    setShowDetail(true);
  };

  const totalSpots = allCachedSpots.summits.length + allCachedSpots.comforts.length;
  const cachedSearches = Object.keys(cache);

  return (
    <div className="relative w-full h-full">
      <MapContainer
        center={center}
        zoom={zoom}
        className="absolute inset-0 z-0"
        style={{ background: '#09090b' }}
        whenCreated={(map) => {
          mapRef.current = map;
          map.on('zoomend', () => {
            setZoom(map.getZoom());
          });
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        
        <MapController 
          onMapClick={handleMapClick} 
          center={center} 
          radius={radius}
        />

        {/* Summit Markers - All cached, filtered by visibility */}
        {showSummits && allCachedSpots.summits.map((spot, idx) => (
          <Marker
            key={`summit-${spot.lat}-${spot.lng}`}
            position={[spot.lat, spot.lng]}
            icon={summitIcon}
            eventHandlers={{
              click: () => handlePointClick(spot)
            }}
          >
            <Popup>
              <div className="font-sans text-sm min-w-[200px]">
                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-200">
                  <Mountain className="w-4 h-4 text-amber-500" />
                  <span className="font-bold text-amber-600">Point Haut</span>
                </div>
                <div className="font-semibold text-gray-800 mb-2">{spot.name}</div>
                <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                  <div>
                    <span className="text-gray-500">Score:</span>
                    <span className="ml-1 font-bold" style={{ color: getScoreColor(spot.score) }}>
                      {spot.score.toFixed(0)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Alt:</span>
                    <span className="ml-1 font-semibold">{spot.elevation}m</span>
                  </div>
                </div>
                {spot.losData && (
                  <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                    <Compass className="w-3 h-3" />
                    <span>{getLOSLabel(spot.losData.clearCount)} ({spot.losData.clearCount}/8)</span>
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Comfort Markers - All cached, filtered by visibility */}
        {showComforts && allCachedSpots.comforts.map((spot, idx) => (
          <Marker
            key={`comfort-${spot.lat}-${spot.lng}`}
            position={[spot.lat, spot.lng]}
            icon={comfortIcon}
            eventHandlers={{
              click: () => handlePointClick(spot)
            }}
          >
            <Popup>
              <div className="font-sans text-sm min-w-[200px]">
                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-200">
                  <TreePine className="w-4 h-4 text-emerald-500" />
                  <span className="font-bold text-emerald-600">Confort</span>
                </div>
                <div className="font-semibold text-gray-800 mb-2">{spot.name}</div>
                <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                  <div>
                    <span className="text-gray-500">Score:</span>
                    <span className="ml-1 font-bold" style={{ color: getScoreColor(spot.score) }}>
                      {spot.score.toFixed(0)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Alt:</span>
                    <span className="ml-1 font-semibold">{spot.elevation}m</span>
                  </div>
                </div>
                {spot.losData && (
                  <div className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                    <Compass className="w-3 h-3" />
                    <span>{getLOSLabel(spot.losData.clearCount)} ({spot.losData.clearCount}/8)</span>
                  </div>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Scanlines overlay */}
      <div className="absolute inset-0 z-10 pointer-events-none">
        <div className="scanlines absolute inset-0" />
      </div>
        
      {/* Initial instruction */}
      {!isCalculating && totalSpots === 0 && (
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20 pointer-events-none">
          <div className="bg-zinc-950/90 backdrop-blur-md border border-cyan-500/50 p-6 rounded-lg">
            <div className="flex items-center gap-3 text-cyan-400">
              <Target className="w-8 h-8" />
              <div>
                <div className="font-semibold text-lg">Prêt à chercher</div>
                <div className="text-sm text-zinc-400 mt-1">
                  Entrez un locator ou cliquez sur la carte
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Info Button - Bottom Left */}
      <div className="absolute bottom-6 left-6 z-20 pointer-events-auto">
        <button
          onClick={() => setShowInfo(true)}
          className="flex items-center gap-2 bg-zinc-950/90 backdrop-blur-md border border-white/10 px-4 py-3 rounded-lg hover:bg-zinc-900 transition-colors"
          data-testid="open-info-button"
        >
          <Info className="w-5 h-5 text-cyan-400" />
          <div className="text-left">
            <div className="text-sm font-medium text-white">Notice & Légende</div>
            <div className="text-xs text-zinc-500">
              {allCachedSpots.summits.length} pts hauts · {allCachedSpots.comforts.length} conforts
            </div>
          </div>
        </button>
      </div>

      {/* Control Panel */}
      <div className="absolute top-6 left-6 z-30 pointer-events-auto">
        <ControlPanel
          radius={radius}
          setRadius={setRadius}
          center={center}
          setCenter={setCenter}
          onSearch={startSearch}
          isCalculating={isCalculating}
          onCenterChange={handleCenterChange}
          cachedSearches={cachedSearches}
          onResetCache={handleResetCache}
          showSummits={showSummits}
          setShowSummits={setShowSummits}
          showComforts={showComforts}
          setShowComforts={setShowComforts}
          summitCount={allCachedSpots.summits.length}
          comfortCount={allCachedSpots.comforts.length}
        />
      </div>

      {/* Detail Panel */}
      {showDetail && selectedPoint && (
        <div className="absolute top-6 right-6 z-30 pointer-events-auto">
          <DetailPanel
            point={selectedPoint}
            onClose={() => setShowDetail(false)}
          />
        </div>
      )}

      {/* Info Modal */}
      <InfoModal isOpen={showInfo} onClose={() => setShowInfo(false)} />

      {/* Progress Bar */}
      {isCalculating && (
        <ProgressBar progress={progress} message={progressMessage} />
      )}
    </div>
  );
};
